// src/pages/settings/components/ChannelPartnerForm.js

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Button,
  Alert,
  Box
} from '@mui/material';

const ChannelPartnerForm = ({ 
  open, 
  onClose, 
  onSubmit, 
  editingPartner, 
  loading = false 
}) => {
  console.log('editingPartner', editingPartner);
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country_code: '+91',
    commission_rate: '',
    remarks: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});

  // Country codes for dropdown
  const countryCodes = [
    { code: '+91', country: 'India' },
    { code: '+1', country: 'USA' },
    { code: '+44', country: 'UK' },
    { code: '+971', country: 'UAE' },
    { code: '+966', country: 'Saudi Arabia' },
    { code: '+65', country: 'Singapore' }
  ];

  // Reset form when editing partner changes
  useEffect(() => {
    if (editingPartner) {
      setFormData({
        name: editingPartner.name,
        email: editingPartner.email,
        phone: editingPartner.phone,
        country_code: editingPartner.country_code,
        commission_rate: editingPartner.commission_rate ?? '',
        remarks: editingPartner.remarks ?? ''
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        country_code: '+91',
        commission_rate: '',
        remarks: ''
      });
    }
    setFieldErrors({});
  }, [editingPartner]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name) {
      errors.name = 'Name is required';
    }
    if (!formData.country_code) {
      errors.country_code = 'Country code is required';
    }
    if (!formData.email && !formData.phone) {
      errors.email = 'Either email or phone must be provided';
      errors.phone = 'Either email or phone must be provided';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (formData.phone && !/^\d{10}$/.test(formData.phone)) {
      errors.phone = 'Phone number must be exactly 10 digits';
    }

    if (formData.commission_rate !== '' && formData.commission_rate != null) {
      const rate = Number(formData.commission_rate);
      if (Number.isNaN(rate)) {
        errors.commission_rate = 'Commission rate must be a valid number';
      } else if (rate < 0) {
        errors.commission_rate = 'Commission rate cannot be negative';
      } else if (rate > 100) {
        errors.commission_rate = 'Commission rate should be <= 100%';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = () => {
    if (validateForm()) {
      const payload = {
        ...formData,
        commission_rate: formData.commission_rate === '' ? null : Number(formData.commission_rate),
        remarks: formData.remarks ? formData.remarks.trim() : null
      };
      onSubmit(payload);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      country_code: '+91',
      commission_rate: '',
      remarks: ''
    });
    setFieldErrors({});
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="xs" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          fontSize: '1.25rem',
          fontWeight: 600,
          color: 'text.primary',
          pb: 1
        }}
      >
        {editingPartner ? 'Edit Channel Partner' : 'Add New Channel Partner'}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
          <TextField
            fullWidth
            label="Full Name"
            required
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!fieldErrors.name}
            helperText={fieldErrors.name}
            disabled={loading}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5
              }
            }}
          />
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
            disabled={loading}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5
              }
            }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl 
              sx={{ 
                minWidth: 140,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5
                }
              }} 
              error={!!fieldErrors.country_code}
            >
              <InputLabel>Country Code</InputLabel>
              <Select
                value={formData.country_code}
                label="Country Code"
                onChange={(e) => handleInputChange('country_code', e.target.value)}
                disabled={loading}
              >
                {countryCodes.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.code} ({country.country})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                handleInputChange('phone', value);
              }}
              error={!!fieldErrors.phone}
              helperText={fieldErrors.phone || 'Enter 10-digit phone number'}
              disabled={loading}
              variant="outlined"
              inputProps={{
                maxLength: 10,
                pattern: '[0-9]*'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5
                }
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="Commission Rate (%)"
              type="number"
              value={formData.commission_rate}
              onChange={(e) => handleInputChange('commission_rate', e.target.value)}
              error={!!fieldErrors.commission_rate}
              helperText={fieldErrors.commission_rate || 'Optional'}
              disabled={loading}
              variant="outlined"
              inputProps={{
                min: 0,
                step: 0.01
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5
                }
              }}
            />
          </Box>

          <TextField
            fullWidth
            label="Remarks"
            multiline
            rows={3}
            value={formData.remarks}
            onChange={(e) => handleInputChange('remarks', e.target.value)}
            disabled={loading}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5
              }
            }}
          />
        </Box>
        
        {/* Validation message for email/phone requirement */}
        {(!formData.email && !formData.phone) && (
          <Alert 
            severity="warning" 
            sx={{ 
              mt: 1,
              borderRadius: 1.5,
              '& .MuiAlert-message': {
                fontSize: '0.875rem'
              }
            }}
          >
            Either email or phone must be provided
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          sx={{ 
            minWidth: 80,
            borderRadius: 1.5,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleFormSubmit}
          variant="contained" 
          disabled={loading || !formData.name}
          sx={{ 
            minWidth: 100,
            borderRadius: 1.5,
            textTransform: 'none',
            fontWeight: 500,
            backgroundColor: '#007050',
            '&:hover': {
              backgroundColor: '#005a3f'
            },
            '&:disabled': {
              backgroundColor: 'rgba(0, 112, 80, 0.3)'
            }
          }}
        >
          {loading ? 'Saving...' : (editingPartner ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChannelPartnerForm;
